import { LoadingState, Alert, Button, Skeleton } from '../components/ui/Primitives';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import PhoneNumberSelector from '../components/PhoneNumberSelector';
import OnboardingGoalSetup from '../components/OnboardingGoalSetup';
import BrowserTestCall from '../components/BrowserTestCall';
import BrandLogo from '../components/BrandLogo';
import {
  getOnboarding,
  getVoiceOptions,
  importOnboardingDocuments,
  importOnboardingWebsite,
  previewVoice,
  saveOnboardingStep,
} from '../services/onboardingApi';
import {
  CALL_RECORDING_CHOICES,
  EXPERIENCES,
  GOALS,
  ONBOARDING_STEPS,
  stepProgress,
} from '../utils/onboardingFlow';
import {
  normalizeSupportedIndustry,
  SUPPORTED_INDUSTRIES,
  supportedIndustryLabel,
} from '../utils/industries';
import {
  normalizeSupportedTimezone,
  suggestedDeviceTimezone,
  SUPPORTED_TIMEZONES,
  supportedTimezoneLabel,
} from '../utils/timezones';
import { logEvent } from '../utils/logger';
import { measureOnboardingCompletion } from '../utils/openaiAdPixel';
import { trackMetaOnboardingCompletion } from '../utils/metaPixel';
import { resetAudioElement } from '../utils/audio';
import { formatAmericanPhoneNumber } from '../utils/phoneNumbers';
import { profileSuggestionIsDuplicate } from '../utils/profileSuggestions';
import { deleteKnowledgeSource } from '../services/knowledgeApi';
import './OnboardingRedesign.css';

const businessBasicsFields = [
  { key: 'businessName', label: 'Business name', required: true, placeholder: 'Acme Dental' },
  { key: 'businessLocation', label: 'Location', placeholder: 'Oakland, California' },
  { key: 'businessPhoneNumber', label: 'Main business phone', placeholder: '(510) 555-0100' },
  { key: 'businessHours', label: 'Business hours', placeholder: 'Monday–Friday, 9am–5pm' },
];

const businessServicesFields = [
  { key: 'businessServices', label: 'Main services', placeholder: 'New patient visits, cleanings, urgent appointments', multiline: true, fullWidth: true, adaptiveRows: true },
  { key: 'businessBio', label: 'Business overview', placeholder: 'A concise description of the business and who it serves', multiline: true, fullWidth: true, adaptiveRows: true },
];

const additionalProfileFields = [
  { key: 'industry', label: 'Industry', control: 'select', options: SUPPORTED_INDUSTRIES, emptyLabel: 'Choose an industry' },
  { key: 'timezone', label: 'Timezone', control: 'select', options: SUPPORTED_TIMEZONES },
  { key: 'staffBio', label: 'Team information', placeholder: 'Names, roles, specialties, or departments callers may ask for', multiline: true, fullWidth: true },
  { key: 'businessFaq', label: 'Common questions and answers', placeholder: 'Parking, pricing, policies, directions, and other common questions', multiline: true, fullWidth: true },
];

const profileFields = [...businessBasicsFields, ...businessServicesFields, ...additionalProfileFields];
const DEVICE_TIMEZONE_SOURCE = 'your device';
const MAX_ONBOARDING_DOCUMENTS = 5;
const fallbackGreeting = businessName => {
  const name = typeof businessName === 'string' ? businessName.trim() : '';
  return name
    ? `Hi, thanks for calling ${name}. I’m the AI receptionist. How can I help you today?`
    : 'Hi, thanks for calling. I’m the AI receptionist. How can I help you today?';
};
const businessProfileSections = [
  { id: 'sources', label: 'Knowledge' },
  { id: 'basics', label: 'Basics' },
  { id: 'services', label: 'Services' },
  { id: 'additional', label: 'Details' },
  { id: 'review', label: 'Review' },
];

const emptyProfile = profileFields.reduce((result, field) => ({ ...result, [field.key]: '' }), {});
const nonEmptyValues = values => Object.fromEntries(
  Object.entries(values || {}).filter(([, value]) => typeof value === 'string' && value.trim()),
);
const enrichableImportFieldLimits = {
  businessHours: 5000,
  businessServices: 10000,
  businessBio: 10000,
  staffBio: 10000,
  businessFaq: 20000,
};

const normalizedSourceNames = source => (source?.sourceNames || [])
  .map(name => name.trim().toLowerCase())
  .filter(Boolean)
  .sort();

const businessImportIdentity = source => {
  const sourceType = source?.sourceType || 'unknown';
  const sourceNames = normalizedSourceNames(source);
  if (sourceType === 'website') {
    let host = '';
    try {
      host = new URL(source?.website || '').hostname.toLowerCase();
    } catch (_error) {
      host = '';
    }
    return `${sourceType}:${host || sourceNames.join('|')}`;
  }
  return source?.sourceId || `${sourceType}:${sourceNames.join('|')}`;
};

const onboardingSourceStatusLabel = status => ({
  queued: 'Queued',
  processing: 'Processing',
  ready: 'Ready',
  failed: 'Needs attention',
}[status] || 'Preparing');

const businessImportsFromState = state => {
  const sources = Array.isArray(state?.business_imports)
    ? state.business_imports
    : state?.business_import ? [state.business_import] : [];
  return sources.reduce((uniqueSources, source) => {
    const identity = businessImportIdentity(source);
    return [...uniqueSources.filter(candidate => businessImportIdentity(candidate) !== identity), source];
  }, []);
};

const sourceDescription = source => (
  source?.suggestionSourceNames?.length ? source.suggestionSourceNames : source?.sourceNames || []
).join(', ') || 'an imported source';

function adaptiveTextareaRows(value) {
  const estimatedLines = String(value || '').split(/\r?\n/).reduce((total, line) => (
    total + Math.max(1, Math.ceil(line.length / 72))
  ), 0);
  return Math.min(12, Math.max(3, estimatedLines));
}

function mergeImportedText(currentValue, suggestedValue, maximum) {
  const lines = [];
  const seen = new Set();
  [currentValue, suggestedValue].forEach(value => {
    String(value || '').split(/\r?\n/).forEach(rawLine => {
      const line = rawLine.trim();
      const comparable = line.toLowerCase().replace(/\s+/g, ' ').replace(/\s*([,;:.!?])\s*/g, '$1');
      if (line && !seen.has(comparable)) {
        seen.add(comparable);
        lines.push(line);
      }
    });
  });
  return lines.join('\n').slice(0, maximum).trim();
}

function mergeImportIntoProfile(profile, source, protectedFields = new Set()) {
  const nextProfile = { ...profile };
  const provenance = {};
  const conflicts = {};
  Object.entries(nonEmptyValues(source?.suggestions)).forEach(([key, suggestedValue]) => {
    const normalizedSuggestion = key === 'timezone'
      ? normalizeSupportedTimezone(suggestedValue)
      : key === 'industry'
        ? normalizeSupportedIndustry(suggestedValue)
        : suggestedValue;
    if (!normalizedSuggestion) return;
    const currentValue = nextProfile[key]?.trim();
    if (profileSuggestionIsDuplicate(key, normalizedSuggestion, currentValue)) return;
    if (!currentValue && !protectedFields.has(key)) {
      nextProfile[key] = normalizedSuggestion;
      provenance[key] = sourceDescription(source);
    } else if (enrichableImportFieldLimits[key] && !protectedFields.has(key)) {
      const enrichedValue = mergeImportedText(
        currentValue,
        normalizedSuggestion,
        enrichableImportFieldLimits[key],
      );
      if (enrichedValue !== currentValue) {
        nextProfile[key] = enrichedValue;
        provenance[key] = sourceDescription(source);
      }
    } else if (currentValue !== normalizedSuggestion.trim()) {
      conflicts[key] = {
        source: sourceDescription(source),
        value: normalizedSuggestion,
      };
    }
  });
  return { profile: nextProfile, provenance, conflicts };
}

function initialProfileState(state, organization) {
  let profile = { ...emptyProfile };
  let provenance = {};
  let conflicts = {};
  businessImportsFromState(state).forEach(source => {
    const merged = mergeImportIntoProfile(profile, source);
    profile = merged.profile;
    provenance = { ...provenance, ...merged.provenance };
    conflicts = { ...conflicts, ...merged.conflicts };
  });
  const savedProfile = state?.business_profile?.profile || {};
  const confirmedValues = Object.fromEntries(profileFields.flatMap(field => {
    const savedValue = savedProfile[field.key] || organization[field.key];
    const value = field.key === 'timezone'
      ? normalizeSupportedTimezone(savedValue)
      : field.key === 'industry'
        ? normalizeSupportedIndustry(savedValue)
        : savedValue;
    return typeof value === 'string' && value.trim() ? [[field.key, value]] : [];
  }));
  Object.keys(confirmedValues).forEach(key => {
    delete provenance[key];
    delete conflicts[key];
  });
  profile = { ...profile, ...confirmedValues };
  const deviceTimezone = suggestedDeviceTimezone();
  if (!profile.timezone && deviceTimezone) {
    profile.timezone = deviceTimezone;
    provenance.timezone = DEVICE_TIMEZONE_SOURCE;
  }
  return {
    profile,
    provenance,
    conflicts,
  };
}

function ChoiceCards({ options, value, onChange }) {
  return (
    <div className="onboarding-choice-grid" role="radiogroup">
      {options.map(option => (
        <button
          key={option.id}
          type="button"
          className={`onboarding-choice${value === option.id ? ' selected' : ''}`}
          role="radio"
          aria-checked={value === option.id}
          disabled={Boolean(option.disabled)}
          onClick={() => onChange(option.id)}
        >
          <span className="onboarding-choice-title">
            {option.title}
          </span>
          <span>{option.description}</span>
        </button>
      ))}
    </div>
  );
}

function OnboardingSourceDeleteDialog({ source, onClose, onConfirm }) {
  const dialogRef = useRef(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');

  useEffect(() => {
    dialogRef.current?.focus();
    function handleKeyDown(event) {
      if (event.key === 'Escape' && !deleting) onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [deleting, onClose]);

  async function confirmDelete() {
    setDeleting(true);
    setDeleteError('');
    const deleted = await onConfirm(source);
    if (deleted) {
      onClose();
      return;
    }
    setDeleteError('We could not delete this source. Please try again.');
    setDeleting(false);
  }

  return (
    <div className="onboarding-delete-backdrop" role="presentation" onMouseDown={event => {
      if (event.target === event.currentTarget && !deleting) onClose();
    }}>
      <section
        ref={dialogRef}
        tabIndex="-1"
        className="onboarding-delete-modal"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="onboarding-delete-source-title"
        aria-describedby="onboarding-delete-source-description"
      >
        <div className="onboarding-delete-heading">
          <div className="onboarding-delete-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" focusable="false"><path d="M4 7h16M9 7V4h6v3m3 0-1 13H7L6 7m4 4v5m4-5v5" /></svg>
          </div>
          <div><span className="say-eyebrow">Delete source</span><h2 id="onboarding-delete-source-title">Remove this from Sayable?</h2></div>
        </div>
        <p id="onboarding-delete-source-description" className="onboarding-delete-description">
          <strong>{source.title}</strong> and its suggestions will be permanently removed. Your Receptionist will no longer use this source when answering callers.
        </p>
        <div className="onboarding-delete-note">This cannot be undone.</div>
        {deleteError ? <Alert tone="danger">{deleteError}</Alert> : null}
        <div className="onboarding-delete-actions">
          <Button variant="secondary" onClick={onClose} disabled={deleting}>Keep source</Button>
          <Button className="onboarding-delete-confirm" variant="danger" loading={deleting} loadingLabel="Deleting…" onClick={confirmDelete}>Delete source</Button>
        </div>
      </section>
    </div>
  );
}

function OnboardingRedesign() {
  const navigate = useNavigate();
  const headingRef = useRef(null);
  const profileStageHeadingRef = useRef(null);
  const documentInputRef = useRef(null);
  const websiteEditorRef = useRef(null);
  const voicePreviewRef = useRef(null);
  const profileRef = useRef({ ...emptyProfile });
  const editedProfileFields = useRef(new Set());
  const [onboarding, setOnboarding] = useState(null);
  const previousOnboarding = useRef(null);
  useEffect(() => {
    measureOnboardingCompletion(previousOnboarding.current, onboarding);
    trackMetaOnboardingCompletion(previousOnboarding.current, onboarding);
    previousOnboarding.current = onboarding;
  }, [onboarding]);
  const [activeStep, setActiveStep] = useState('goal');
  const [profileSectionIndex, setProfileSectionIndex] = useState(0);
  const [returnToProfileReview, setReturnToProfileReview] = useState(false);
  const [answers, setAnswers] = useState({ profile: { ...emptyProfile }, website: '' });
  const [voices, setVoices] = useState([]);
  const [setupBusy, setSetupBusy] = useState(false);
  const [setupReview, setSetupReview] = useState(false);
  const [setupDraftSaving, setSetupDraftSaving] = useState(false);
  const refreshSetupSnapshot = useCallback(async () => {
    setOnboarding(await getOnboarding());
  }, []);
  const [documents, setDocuments] = useState([]);
  const [documentSelectionNotice, setDocumentSelectionNotice] = useState('');
  const [fieldSources, setFieldSources] = useState({});
  const [editedSuggestedFields, setEditedSuggestedFields] = useState({});
  const [importConflicts, setImportConflicts] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState('');
  const [voicePreviewState, setVoicePreviewState] = useState('idle');
  const [voicePreviewError, setVoicePreviewError] = useState('');
  const [greetingSuggestion, setGreetingSuggestion] = useState('');
  const [error, setError] = useState('');
  const [sourcePendingDeletion, setSourcePendingDeletion] = useState(null);
  const [websitePendingImport, setWebsitePendingImport] = useState(false);

  const steps = onboarding?.steps || ONBOARDING_STEPS;
  const progress = stepProgress(activeStep, steps);
  const selectedGoals = answers.selectedGoals || [answers.primaryGoal || 'message'];
  const aiRecommendations = onboarding?.recommendations || onboarding?.stepState?.recommendations || {};
  const businessImports = useMemo(
    () => businessImportsFromState(onboarding?.stepState),
    [onboarding],
  );
  const importedSourceItems = useMemo(() => businessImports
    .flatMap(source => (source.sourceNames || []).map(name => ({
      name,
      sourceId: source.sourceId,
      sourceType: source.sourceType,
      status: source.status,
    })))
    .filter((source, index, sources) => sources.findIndex(candidate => (
      candidate.sourceType === source.sourceType
      && candidate.name.trim().toLowerCase() === source.name.trim().toLowerCase()
    )) === index), [businessImports]);
  const hasProcessingSources = businessImports.some(source => !['ready', 'failed'].includes(source.status));
  const hasFailedSources = businessImports.some(source => source.status === 'failed');
  const processingSourceCount = importedSourceItems.filter(source => !['ready', 'failed'].includes(source.status)).length;
  const failedSourceCount = importedSourceItems.filter(source => source.status === 'failed').length;
  const suggestedFieldKeys = [...new Set([...Object.keys(fieldSources), ...Object.keys(importConflicts)])];
  const aiSuggestedFieldKeys = suggestedFieldKeys.filter(key => (
    Boolean(importConflicts[key]) || fieldSources[key] !== DEVICE_TIMEZONE_SOURCE
  ));
  const detectedFieldKeys = suggestedFieldKeys.filter(key => (
    !importConflicts[key] && fieldSources[key] === DEVICE_TIMEZONE_SOURCE
  ));
  const suggestedFieldLabels = profileFields
    .filter(field => suggestedFieldKeys.includes(field.key))
    .map(field => field.label);
  const profileSection = businessProfileSections[profileSectionIndex];
  const hasReadySuggestionSource = businessImports.some(source => (
    source.status === 'ready'
    && Object.values(source.suggestions || {}).some(value => String(value || '').trim())
  ));
  const hasLimitedWebsiteCoverage = businessImports.some(source => (
    source.sourceType === 'website' && source.coverageLimited
  ));
  const hasPendingSourceInput = documents.length > 0 || websitePendingImport;
  const suggestionsReadyForReview = aiSuggestedFieldKeys.length > 0 && hasReadySuggestionSource;

  useEffect(() => {
    const token = localStorage.getItem('jwt_authorization');
    if (!token) {
      navigate('/signin');
      return;
    }
    async function load() {
      try {
        const [result, voiceOptions] = await Promise.all([getOnboarding(), getVoiceOptions()]);
        const state = result.stepState || {};
        const organization = result.organization || {};
        const initialProfile = initialProfileState(state, organization);
        const returnGoal = new URLSearchParams(window.location.search).get('setupGoal');
        const resumingConnection = ['custom', 'appointment'].includes(returnGoal)
          && result.selectedGoals?.includes(returnGoal) && result.status !== 'completed';
        const currentStepKey = resumingConnection ? 'goal_setup' : result.currentStepKey || 'goal';
        const shouldSuggestGreeting = ONBOARDING_STEPS.indexOf(currentStepKey) >= ONBOARDING_STEPS.indexOf('personalize');
        const recommendations = result.recommendations || state.recommendations || {};
        const suggestedGreeting = !organization.greeting && shouldSuggestGreeting
          ? recommendations.greeting || ''
          : '';
        setOnboarding(result);
        setActiveStep(currentStepKey);
        setVoices(voiceOptions);
        setGreetingSuggestion(suggestedGreeting);
        setAnswers({
          primaryGoal: result.primaryGoal || state.goal?.primaryGoal || 'message',
          selectedGoals: result.selectedGoals?.length ? result.selectedGoals : [result.primaryGoal || 'message'],
          experienceLevel: result.experienceLevel || state.experience?.experienceLevel || 'first_time',
          website: state.business_profile?.website || organization.businessWebsite || '',
          profile: initialProfile.profile,
          greeting: organization.greeting || suggestedGreeting || (
            shouldSuggestGreeting ? fallbackGreeting(initialProfile.profile.businessName) : ''
          ),
          voiceId: organization.voiceId || (voiceOptions[0]?.id ? String(voiceOptions[0].id) : ''),
          callRecording: typeof state.call_recording?.enabled === 'boolean'
            ? (state.call_recording.enabled ? 'enabled' : 'disabled')
            : '',
        });
        profileRef.current = initialProfile.profile;
        setFieldSources(initialProfile.provenance);
        setImportConflicts(initialProfile.conflicts);
        logEvent({ event: { label: 'onboarding_started', data: { onboardingVersion: result.version, flowStatus: result.status } } });
      } catch (requestError) {
        if (requestError.response?.status === 401) {
          localStorage.removeItem('jwt_authorization');
          navigate('/signin');
          return;
        }
        setError('We couldn’t load your setup. Try again without losing your progress.');
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [navigate]);

  useEffect(() => {
    headingRef.current?.focus();
    if (!loading) logEvent({ event: { label: 'onboarding_step_viewed', data: { stepKey: activeStep } } });
  }, [activeStep, loading]);

  useEffect(() => {
    if (activeStep !== 'business_profile' || loading) return;
    profileStageHeadingRef.current?.focus();
    logEvent({ event: { label: 'onboarding_profile_section_viewed', data: { stepKey: 'business_profile', value: profileSection.id } } });
  }, [activeStep, loading, profileSection.id]);

  useEffect(() => {
    if (!hasProcessingSources) return undefined;
    let cancelled = false;
    const timer = window.setInterval(async () => {
      try {
        const result = await getOnboarding();
        if (!cancelled) applyImportedProfileSnapshot(result);
      } catch (_requestError) {
        // Keep the current source state visible; the next poll can recover.
      }
    }, 3500);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [hasProcessingSources]);

  useEffect(() => {
    const editor = websiteEditorRef.current;
    const website = answers.website || '';
    if (editor && document.activeElement !== editor && editor.textContent !== website) {
      editor.textContent = website;
    }
  }, [answers.website]);

  useEffect(() => {
    const audio = voicePreviewRef.current;
    return () => resetAudioElement(audio);
  }, [activeStep]);

  function updateProfile(key, value) {
    editedProfileFields.current.add(key);
    if (fieldSources[key]) {
      setEditedSuggestedFields(current => ({ ...current, [key]: true }));
    }
    const profile = { ...profileRef.current, [key]: value };
    profileRef.current = profile;
    setAnswers(current => ({ ...current, profile }));
  }

  function updateWebsite(value) {
    setAnswers(current => ({ ...current, website: value }));
    const normalizedValue = String(value || '').trim();
    const matchesImportedWebsite = businessImports.some(source => (
      source.sourceType === 'website'
      && String(source.website || '').replace(/\/$/, '') === normalizedValue.replace(/\/$/, '')
    ));
    setWebsitePendingImport(Boolean(normalizedValue) && !matchesImportedWebsite);
  }

  function stopVoicePreview() {
    resetAudioElement(voicePreviewRef.current);
    setVoicePreviewState('idle');
  }

  function updateGreeting(value) {
    stopVoicePreview();
    setVoicePreviewError('');
    setAnswers(current => ({ ...current, greeting: value }));
  }

  function restoreGreetingSuggestion() {
    if (greetingSuggestion) updateGreeting(greetingSuggestion);
  }

  function updateVoice(value) {
    stopVoicePreview();
    setVoicePreviewError('');
    setAnswers(current => ({ ...current, voiceId: value }));
  }

  async function startVoicePreview() {
    if (!answers.voiceId || !answers.greeting?.trim()) return;
    stopVoicePreview();
    setVoicePreviewState('loading');
    setVoicePreviewError('');
    try {
      const previewUrl = await previewVoice(answers.voiceId, answers.greeting.trim());
      if (!previewUrl || !voicePreviewRef.current) throw new Error('Preview URL missing');
      voicePreviewRef.current.src = process.env.REACT_APP_ENV === 'dev'
        ? previewUrl.replace('https://', 'http://')
        : previewUrl;
    } catch (_previewError) {
      setVoicePreviewState('idle');
      setVoicePreviewError('The preview could not be played. Try again in a moment.');
    }
  }

  async function playLoadedVoicePreview() {
    try {
      await voicePreviewRef.current?.play();
      setVoicePreviewState('playing');
    } catch (_playError) {
      setVoicePreviewState('idle');
      setVoicePreviewError('Your browser blocked the preview. Select Play greeting to try again.');
    }
  }

  function entryModeForProfile() {
    const sourceTypes = new Set(businessImports.map(source => source.sourceType));
    if (sourceTypes.size > 1) return 'combined';
    if (sourceTypes.has('website')) return 'website';
    if (sourceTypes.has('documents')) return 'documents';
    return 'manual';
  }

  function payloadForStep(stepKey) {
    if (stepKey === 'goal') return { primaryGoal: selectedGoals[0], selectedGoals };
    if (stepKey === 'experience') return { experienceLevel: answers.experienceLevel };
    if (stepKey === 'business_profile') return {
      entryMode: entryModeForProfile(),
      website: answers.website || '',
      profile: answers.profile,
    };
    if (stepKey === 'personalize') return { greeting: answers.greeting, voiceId: String(answers.voiceId) };
    if (stepKey === 'call_recording') return { enabled: answers.callRecording === 'enabled' };
    if (stepKey === 'goal_setup') return { businessFaq: answers.profile.businessFaq || '' };
    if (stepKey === 'review') return { accepted: true };
    return null;
  }

  function canContinue() {
    if (activeStep === 'goal') return selectedGoals.length > 0;
    if (activeStep === 'goal_setup') return !setupBusy && !setupDraftSaving;
    if (activeStep === 'experience') return Boolean(answers.experienceLevel);
    if (activeStep === 'business_profile') {
      if (profileSection.id === 'sources') {
        if (hasPendingSourceInput || importing) return false;
        if (hasProcessingSources) return false;
      }
      return profileSection.id !== 'basics' || Boolean(answers.profile.businessName?.trim());
    }
    if (activeStep === 'personalize') return Boolean(answers.greeting?.trim() && answers.voiceId);
    if (activeStep === 'call_recording') return Boolean(answers.callRecording);
    return true;
  }

  async function saveSetupProgress() {
    setSetupDraftSaving(true);
    try {
      const result = await saveOnboardingStep('goal_setup', { ...payloadForStep('goal_setup'), draft: true });
      setOnboarding(result);
      return true;
    } catch (requestError) {
      setError(requestError.response?.data?.error?.message || 'Setup progress could not be saved. Please try again.');
      return false;
    } finally {
      setSetupDraftSaving(false);
    }
  }

  async function saveAndExit() {
    if (activeStep === 'goal_setup') {
      if (!(await saveSetupProgress())) return;
    } else if (['goal', 'experience', 'personalize', 'call_recording'].includes(activeStep)) {
      if (!canContinue()) {
        setError('Complete this choice before saving and exiting.');
        return;
      }
      setSaving(true);
      try {
        await saveOnboardingStep(activeStep, payloadForStep(activeStep));
      } catch (requestError) {
        setError(requestError.response?.data?.error?.message || 'Your progress could not be saved. Please try again.');
        return;
      } finally {
        setSaving(false);
      }
    }
    navigate('/overview');
  }

  async function continueStep() {
    if (activeStep === 'business_profile' && returnToProfileReview) {
      setError('');
      setReturnToProfileReview(false);
      setProfileSectionIndex(businessProfileSections.length - 1);
      return;
    }
    if (activeStep === 'business_profile' && profileSectionIndex < businessProfileSections.length - 1) {
      setError('');
      setProfileSectionIndex(current => current + 1);
      return;
    }
    const payload = payloadForStep(activeStep);
    if (!payload) return;
    setSaving(true);
    setError('');
    const started = Date.now();
    try {
      const result = await saveOnboardingStep(activeStep, payload);
      setOnboarding(result);
      if (activeStep === 'business_profile' && !answers.greeting?.trim()) {
        const suggestedGreeting = result.recommendations?.greeting || '';
        setGreetingSuggestion(suggestedGreeting);
        setAnswers(current => ({
          ...current,
          greeting: suggestedGreeting || fallbackGreeting(answers.profile.businessName),
        }));
      }
      setActiveStep(result.currentStepKey || steps[Math.min(progress.index + 1, steps.length - 1)]);
      logEvent({ event: { label: 'onboarding_step_completed', data: { stepKey: activeStep, durationMs: Date.now() - started, onboardingVersion: result.version } } });
    } catch (requestError) {
      setError(requestError.response?.data?.error?.message || 'That step could not be saved. Check your answers and try again.');
      if (activeStep === 'goal_setup' || activeStep === 'review') {
        try { setOnboarding(await getOnboarding()); } catch (_error) { /* Keep the saved snapshot until retry. */ }
      }
    } finally {
      setSaving(false);
    }
  }

  function applyImportedSource(source) {
    const importedTimezone = normalizeSupportedTimezone(source?.suggestions?.timezone);
    const canReplaceDeviceTimezone = importedTimezone
      && fieldSources.timezone === DEVICE_TIMEZONE_SOURCE
      && !editedProfileFields.current.has('timezone');
    const profileBeforeImport = canReplaceDeviceTimezone
      ? { ...profileRef.current, timezone: '' }
      : profileRef.current;
    const merged = mergeImportIntoProfile(profileBeforeImport, source, editedProfileFields.current);
    profileRef.current = merged.profile;
    setAnswers(current => ({
      ...current,
      website: source.website || current.website,
      profile: merged.profile,
    }));
    setFieldSources(current => ({ ...current, ...merged.provenance }));
    setEditedSuggestedFields(current => {
      const next = { ...current };
      Object.keys(merged.provenance).forEach(key => { delete next[key]; });
      return next;
    });
    setImportConflicts(current => ({ ...current, ...merged.conflicts }));
  }

  function applyImportedProfileSnapshot(result) {
    const rebuilt = initialProfileState(result.stepState || {}, result.organization || {});
    const profile = { ...rebuilt.profile };
    const provenance = { ...rebuilt.provenance };
    const conflicts = { ...rebuilt.conflicts };
    editedProfileFields.current.forEach(key => {
      profile[key] = profileRef.current[key];
      delete provenance[key];
      delete conflicts[key];
    });
    profileRef.current = profile;
    setAnswers(current => ({ ...current, profile }));
    setFieldSources(provenance);
    setImportConflicts(conflicts);
    setEditedSuggestedFields(current => Object.fromEntries(
      Object.entries(current).filter(([key]) => provenance[key]),
    ));
    setOnboarding(result);
  }

  async function deleteImportedSource(source) {
    try {
      await deleteKnowledgeSource(source.id);
      let result;
      try {
        result = await getOnboarding();
      } catch (_refreshError) {
        const nextImports = businessImports.filter(imported => (
          String(imported.sourceId || '') !== String(source.id)
        ));
        const stepState = { ...(onboarding?.stepState || {}) };
        if (nextImports.length) {
          stepState.business_imports = nextImports;
          stepState.business_import = nextImports[nextImports.length - 1];
        } else {
          delete stepState.business_imports;
          delete stepState.business_import;
        }
        result = { ...onboarding, stepState };
      }
      applyImportedProfileSnapshot(result);
      logEvent({ event: { label: 'onboarding_source_deleted', data: { stepKey: 'business_profile', value: source.sourceType } } });
      return true;
    } catch (_requestError) {
      return false;
    }
  }

  function selectDocuments(fileList) {
    const selected = Array.from(fileList || []);
    const unique = [...documents, ...selected].filter((file, index, files) => (
      files.findIndex(candidate => (
        candidate.name === file.name
        && candidate.size === file.size
        && candidate.lastModified === file.lastModified
      )) === index
    ));
    const accepted = unique.slice(0, MAX_ONBOARDING_DOCUMENTS);
    setDocuments(accepted);
    setDocumentSelectionNotice(unique.length > MAX_ONBOARDING_DOCUMENTS
      ? `You can add up to ${MAX_ONBOARDING_DOCUMENTS} documents. The first ${MAX_ONBOARDING_DOCUMENTS} are selected.`
      : '');
    if (documentInputRef.current) documentInputRef.current.value = '';
  }

  function removeDocument(index) {
    setDocuments(current => current.filter((_file, fileIndex) => fileIndex !== index));
    setDocumentSelectionNotice('');
  }

  function acceptImportedSuggestion(key) {
    const conflict = importConflicts[key];
    if (!conflict) return;
    editedProfileFields.current.add(key);
    const profile = { ...profileRef.current, [key]: conflict.value };
    profileRef.current = profile;
    setAnswers(current => ({ ...current, profile }));
    setFieldSources(current => ({ ...current, [key]: conflict.source }));
    setEditedSuggestedFields(current => {
      const next = { ...current };
      delete next[key];
      return next;
    });
    setImportConflicts(current => {
      const next = { ...current };
      delete next[key];
      return next;
    });
  }

  async function uploadDocuments() {
    if (!documents.length) return;
    setImporting('documents');
    setError('');
    try {
      const result = await importOnboardingDocuments(documents);
      const imported = result.stepState?.business_import || {};
      setOnboarding(result);
      applyImportedSource(imported);
      setDocuments([]);
      setDocumentSelectionNotice('');
      logEvent({ event: { label: 'onboarding_document_import_succeeded', data: { stepKey: 'business_profile', value: imported.sourceCount || 0 } } });
    } catch (requestError) {
      setError(requestError.response?.data?.error?.message || 'Those documents could not be imported. Try a PDF, Word, text, Markdown, or CSV file.');
      logEvent({ event: { label: 'onboarding_document_import_failed', data: { stepKey: 'business_profile', errorCode: requestError.response?.data?.error?.code || 'UNKNOWN' } } });
    } finally {
      setImporting('');
    }
  }

  async function importWebsite() {
    if (!answers.website?.trim()) return;
    setImporting('website');
    setError('');
    try {
      const result = await importOnboardingWebsite(answers.website);
      const imported = result.stepState?.business_import || {};
      setOnboarding(result);
      applyImportedSource(imported);
      setWebsitePendingImport(false);
      logEvent({ event: { label: 'onboarding_website_import_succeeded', data: { stepKey: 'business_profile', value: imported.sourceCount || 0 } } });
    } catch (requestError) {
      setError(requestError.response?.data?.error?.message || 'That website could not be imported. Check the URL or use documents instead.');
      logEvent({ event: { label: 'onboarding_website_import_failed', data: { stepKey: 'business_profile', errorCode: requestError.response?.data?.error?.code || 'UNKNOWN' } } });
    } finally {
      setImporting('');
    }
  }

  function renderProfileField(field) {
    const fieldId = `onboarding-profile-${field.key}`;
    const source = fieldSources[field.key];
    const isAiSuggestion = Boolean(source && source !== DEVICE_TIMEZONE_SOURCE);
    const isEditedSuggestion = Boolean(editedSuggestedFields[field.key]);
    const conflict = importConflicts[field.key];
    return (
      <div key={field.key} className={`onboarding-profile-field${field.fullWidth ? ' full-width' : ''}${source ? ' suggested' : ''}${conflict ? ' has-conflict' : ''}`}>
        <div className="onboarding-profile-field-heading">
          <label htmlFor={fieldId}>{field.label}{field.required ? ' *' : ''}</label>
          {source ? (
            <span className={isEditedSuggestion ? 'edited' : ''}>
              {isAiSuggestion
                ? isEditedSuggestion ? 'Edited AI suggestion' : 'AI suggested'
                : isEditedSuggestion ? 'Edited detected value' : 'Detected'}
            </span>
          ) : null}
        </div>
        {field.control === 'select' ? (
          <select id={fieldId} value={answers.profile[field.key] || ''} onChange={event => updateProfile(field.key, event.target.value)}>
            <option value="">{field.emptyLabel || 'Choose a timezone'}</option>
            {field.options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        ) : field.multiline
          ? <textarea id={fieldId} className={field.adaptiveRows ? 'onboarding-adaptive-textarea' : undefined} rows={field.adaptiveRows ? adaptiveTextareaRows(answers.profile[field.key]) : 3} value={answers.profile[field.key] || ''} placeholder={field.placeholder} onChange={event => updateProfile(field.key, event.target.value)} />
          : <input id={fieldId} value={answers.profile[field.key] || ''} placeholder={field.placeholder} onChange={event => updateProfile(field.key, event.target.value)} />}
        {source ? <small className="onboarding-field-source">{isEditedSuggestion ? 'Edited from' : 'Filled from'} {source}</small> : null}
        {conflict ? (
          <div className="onboarding-field-conflict" role="note">
            <div><strong>New AI suggestion from {conflict.source}</strong><p>“{conflict.value}”</p></div>
            <button type="button" onClick={() => acceptImportedSuggestion(field.key)}>Use this suggestion</button>
          </div>
        ) : null}
      </div>
    );
  }

  function renderProfileSectionSummary(section, sectionIndex) {
    let title = '';
    let description = '';
    if (section.id === 'basics') {
      title = answers.profile.businessName || 'Business basics';
      description = [answers.profile.businessLocation, answers.profile.businessPhoneNumber, answers.profile.businessHours]
        .filter(Boolean)
        .join(' · ') || 'Only the business name is required';
    } else if (section.id === 'services') {
      title = answers.profile.businessServices || 'No services added';
      description = answers.profile.businessBio || 'You can add this later';
    } else {
      title = [supportedIndustryLabel(answers.profile.industry), supportedTimezoneLabel(answers.profile.timezone)]
        .filter(Boolean)
        .join(' · ') || 'Additional details';
      description = [
        answers.profile.staffBio?.trim() ? 'Team information added' : '',
        answers.profile.businessFaq?.trim() ? 'Common questions added' : '',
      ].filter(Boolean).join(' · ') || 'No other details added';
    }
    return (
      <article className="onboarding-profile-summary" key={section.id}>
        <span className="onboarding-profile-summary-check" aria-hidden="true">✓</span>
        <div><small>{section.label}</small><strong>{title}</strong><p>{description}</p></div>
        <button type="button" onClick={() => { setReturnToProfileReview(true); setProfileSectionIndex(sectionIndex); }} aria-label={`Edit ${section.label}`}>Edit</button>
      </article>
    );
  }

  async function waitForCallCompletion() {
    setSaving(true);
    setError('');
    let latestOnboarding = onboarding;
    try {
      for (let attempt = 0; attempt < 6; attempt += 1) {
        latestOnboarding = await getOnboarding();
        setOnboarding(latestOnboarding);
        setActiveStep(latestOnboarding.currentStepKey || 'first_call');
        if (latestOnboarding.status === 'completed' || latestOnboarding.readiness?.firstCallCompleted) return;
        if (attempt < 5) await new Promise(resolve => window.setTimeout(resolve, 2000));
      }
      setError('Your call is complete. We are still finishing the call record. Check again in a moment.');
    } catch (_requestError) {
      setError('Your call is complete, but setup could not confirm it yet. Check again in a moment.');
    } finally {
      setSaving(false);
    }
  }

  async function handlePhoneNumberClaimed(selectedNumber) {
    setSaving(true);
    setError('');
    try {
      const result = await getOnboarding();
      setOnboarding(result);
      setActiveStep(result.currentStepKey || 'first_call');
      logEvent({ event: { label: 'onboarding_phone_number_selected', data: { region: selectedNumber.region || '' } } });
    } catch (_requestError) {
      // The number is already claimed; never reopen selection if setup refresh fails.
      navigate('/overview', { replace: true });
    } finally {
      setSaving(false);
    }
  }

  async function goBack() {
    if (activeStep === 'goal_setup' && !(await saveSetupProgress())) return;
    if (activeStep === 'business_profile' && returnToProfileReview) {
      setReturnToProfileReview(false);
      setProfileSectionIndex(businessProfileSections.length - 1);
      return;
    }
    if (activeStep === 'business_profile' && profileSectionIndex > 0) {
      setProfileSectionIndex(current => current - 1);
      return;
    }
    const previous = steps[progress.index - 1];
    if (previous) setActiveStep(previous);
  }

  if (loading) {
    return <div className="onboarding-redesign-loading"><Skeleton width="36%" height={18} /><Skeleton width="72%" height={48} /><Skeleton height={280} /></div>;
  }

  const organization = onboarding?.organization || {};
  if (activeStep === 'phone_number' && organization.provisionedNumber) {
    return <Navigate to="/overview" replace />;
  }
  const continueButtonLabel = activeStep === 'business_profile' && returnToProfileReview
    ? 'Return to review'
    : activeStep === 'review'
    ? 'Publish Receptionist and choose a number'
    : activeStep === 'business_profile' && profileSection.id === 'sources'
      ? documents.length
        ? 'Import selected documents first'
        : websitePendingImport
          ? 'Import website first'
          : hasProcessingSources
            ? `Preparing ${processingSourceCount || 1} source${(processingSourceCount || 1) === 1 ? '' : 's'}…`
            : suggestionsReadyForReview
              ? `Review ${suggestedFieldKeys.length} suggestion${suggestedFieldKeys.length === 1 ? '' : 's'}`
              : detectedFieldKeys.length
                ? `Review ${detectedFieldKeys.length} detected detail${detectedFieldKeys.length === 1 ? '' : 's'}`
              : 'Enter business details'
    : activeStep === 'business_profile' && profileSection.id === 'additional'
      ? 'Review profile'
      : activeStep === 'business_profile' && profileSection.id === 'review'
        ? 'Confirm business profile'
        : 'Continue';

  return (
    <div className="onboarding-redesign-page">
      <header className="onboarding-topbar">
        <a className="onboarding-brand" href="/" aria-label="Sayable home"><BrandLogo decorative /></a>
        <button type="button" disabled={saving || (activeStep === 'goal_setup' && (setupBusy || setupDraftSaving))} onClick={saveAndExit}>Save and exit</button>
      </header>

      {onboarding?.status !== 'completed' ? (
        <div className="onboarding-progress" aria-label={`Step ${progress.number} of ${progress.total}`}>
          <div><span>Step {progress.number} of {progress.total}</span><span>About {Math.max(1, 8 - progress.number)} min left</span></div>
          <progress value={progress.percent} max="100">{progress.percent}%</progress>
        </div>
      ) : null}

      <main className="onboarding-conversation">
        <section className={`onboarding-dialogue${activeStep === 'goal_setup' ? ' onboarding-dialogue-setup' : ''}`} aria-live="polite">
          {activeStep === 'goal' && <>
            <span className="say-eyebrow">Let’s build your Receptionist</span>
            <h1 ref={headingRef} tabIndex="-1">What should Sayable.ai handle for callers?</h1>
            <p>Choose one or more outcomes. Setup will collect and configure what each one needs.</p>
            <div className="onboarding-choice-grid" role="group" aria-label="Caller outcomes">{GOALS.map(option => <button type="button" key={option.id} className={`onboarding-choice${selectedGoals.includes(option.id) ? ' selected' : ''}`} aria-pressed={selectedGoals.includes(option.id)} onClick={() => setAnswers(current => {
              const choices = current.selectedGoals || [current.primaryGoal || 'message'];
              const next = choices.includes(option.id) ? choices.filter(goal => goal !== option.id) : [...choices, option.id];
              return { ...current, selectedGoals: next, primaryGoal: next[0] || '' };
            })}><span className="onboarding-choice-title">{option.title}</span><span>{option.description}</span></button>)}</div>
          </>}

          {activeStep === 'experience' && <>
            <span className="say-eyebrow">A little context</span>
            <h1 ref={headingRef} tabIndex="-1">How familiar are you with AI receptionists?</h1>
            <p>Your answer only changes how much explanation we show.</p>
            <ChoiceCards options={EXPERIENCES} value={answers.experienceLevel} onChange={value => setAnswers(current => ({ ...current, experienceLevel: value }))} />
          </>}

          {activeStep === 'business_profile' && <>
            <span className="say-eyebrow">Business profile</span>
            <h1 ref={headingRef} tabIndex="-1">{profileSection.id === 'review' ? 'Review your business profile' : 'What should your Receptionist know about your business?'}</h1>
            <p>{profileSection.id === 'review' ? 'Check the information your Receptionist will use. Edit only what needs attention.' : 'We’ll take this one section at a time. Imported details carry forward automatically, and every answer stays editable.'}</p>
            <ol className="onboarding-profile-progress" aria-label={`Business profile section ${profileSectionIndex + 1} of ${businessProfileSections.length}`}>
              {businessProfileSections.map((section, index) => (
                <li key={section.id} className={index === profileSectionIndex ? 'current' : index < profileSectionIndex ? 'complete' : ''} aria-current={index === profileSectionIndex ? 'step' : undefined}>
                  <span>{index < profileSectionIndex ? '✓' : index + 1}</span>{section.label}
                </li>
              ))}
            </ol>
            {returnToProfileReview ? <div className="onboarding-profile-editing" role="status">Editing {profileSection.label}. Your other sections are unchanged.</div> : null}
            <div className="onboarding-profile-stage" key={profileSection.id}>
              {profileSection.id === 'sources' && <>
                <div className="onboarding-profile-stage-intro">
                  <span>1 of 5</span>
                  <h2 ref={profileStageHeadingRef} tabIndex="-1">Start with what you already have</h2>
                  <p>Add a website, documents, or both. This is optional—you can enter everything manually in the next sections.</p>
                </div>
                <div className="onboarding-source-grid">
                  <div className="onboarding-upload-panel onboarding-website-panel">
                    <label id="onboarding-website-label"><strong>Import your website</strong><span>We’ll scan the most relevant public pages.</span></label>
                    <div className="onboarding-import-row">
                      <div
                        id="onboarding-website"
                        ref={websiteEditorRef}
                        className="onboarding-website-editor"
                        role="textbox"
                        aria-labelledby="onboarding-website-label"
                        contentEditable={!Boolean(importing)}
                        suppressContentEditableWarning
                        spellCheck={false}
                        data-lpignore="true"
                        data-1p-ignore="true"
                        data-bwignore="true"
                        data-form-type="other"
                        data-placeholder="https://example.com"
                        onInput={event => updateWebsite(event.currentTarget.textContent || '')}
                        onPaste={event => {
                          event.preventDefault();
                          document.execCommand('insertText', false, event.clipboardData.getData('text/plain').trim());
                        }}
                        onKeyDown={event => {
                          if (event.key === 'Enter') {
                            event.preventDefault();
                            if (answers.website?.trim() && !importing) importWebsite();
                          }
                        }}
                      />
                      <Button variant="secondary" onClick={importWebsite} disabled={!answers.website?.trim() || Boolean(importing)} loading={importing === 'website'} loadingLabel={"Reading website…"}>{'Import website'}</Button>
                    </div>
                  </div>
                  <div className="onboarding-upload-panel">
                    <label htmlFor="onboarding-documents"><strong>Upload business documents</strong><span>PDF, Word, text, Markdown, or CSV. Up to 5 files.</span></label>
                    <label className={`onboarding-document-picker${documents.length ? ' has-files' : ''}${documents.length >= MAX_ONBOARDING_DOCUMENTS || importing ? ' disabled' : ''}`} htmlFor="onboarding-documents" aria-disabled={documents.length >= MAX_ONBOARDING_DOCUMENTS || Boolean(importing)}>
                      <span className="onboarding-document-picker-button">
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 16V4m0 0L7.5 8.5M12 4l4.5 4.5M5 14v4.5A1.5 1.5 0 0 0 6.5 20h11a1.5 1.5 0 0 0 1.5-1.5V14" /></svg>
                        {documents.length >= MAX_ONBOARDING_DOCUMENTS ? 'Files selected' : documents.length ? 'Add files' : 'Choose files'}
                      </span>
                      <span className="onboarding-document-picker-summary">
                        {documents.length ? `${documents.length} of ${MAX_ONBOARDING_DOCUMENTS} selected` : `Select up to ${MAX_ONBOARDING_DOCUMENTS} at once`}
                      </span>
                      <input ref={documentInputRef} className="onboarding-document-input" id="onboarding-documents" type="file" multiple accept=".pdf,.docx,.txt,.md,.csv" disabled={documents.length >= MAX_ONBOARDING_DOCUMENTS || Boolean(importing)} aria-describedby={documentSelectionNotice ? 'onboarding-document-notice' : undefined} onChange={event => selectDocuments(event.target.files)} />
                    </label>
                    {documentSelectionNotice ? <p className="onboarding-document-notice" id="onboarding-document-notice" role="status">{documentSelectionNotice}</p> : null}
                    {documents.length ? (
                      <ul className="onboarding-pending-files" aria-label="Selected documents">
                        {documents.map((file, index) => (
                          <li key={`${file.name}-${file.size}-${file.lastModified}`}>
                            <span className="onboarding-pending-file-number" aria-hidden="true">{index + 1}</span>
                            <span className="onboarding-pending-file-name" title={file.name}>{file.name}</span>
                            <button type="button" aria-label={`Remove ${file.name}`} disabled={Boolean(importing)} onClick={() => removeDocument(index)}>Remove</button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {documents.length && importing !== 'documents' ? <p className="onboarding-import-required" role="status">Selected documents are not imported yet. Import them before moving on.</p> : null}
                    <Button onClick={uploadDocuments} disabled={!documents.length || Boolean(importing)} loading={importing === 'documents'} loadingLabel={"Reading documents…"}>{documents.length ? `Import ${documents.length} selected document${documents.length === 1 ? '' : 's'}` : 'Import documents'}</Button>
                  </div>
                </div>
                {importing ? (
                  <LoadingState label={importing === 'website' ? 'Reading your website…' : 'Reading your documents…'} />
                ) : null}
                {!importing && (suggestedFieldKeys.length || businessImports.length) ? (
                  <div className="onboarding-suggestion-ready" role="status">
                    <span aria-hidden="true">{suggestedFieldKeys.length}</span>
                    <div>
                      <strong>{hasProcessingSources
                        ? `Preparing suggestions from ${processingSourceCount || 1} remaining source${(processingSourceCount || 1) === 1 ? '' : 's'}`
                        : aiSuggestedFieldKeys.length
                        ? `${aiSuggestedFieldKeys.length} relevant AI suggestion${aiSuggestedFieldKeys.length === 1 ? '' : 's'}${detectedFieldKeys.length ? ` and ${detectedFieldKeys.length} detected default` : ''} ready to review`
                        : detectedFieldKeys.length
                          ? `${detectedFieldKeys.length} detail${detectedFieldKeys.length === 1 ? '' : 's'} detected from your device`
                          : 'Sources saved'}</strong>
                      <p>{hasProcessingSources
                        ? 'Review suggestions will be available after every source finishes.'
                        : hasLimitedWebsiteCoverage
                          ? 'We found only a few useful details. You can add documents or enter anything else manually.'
                          : suggestedFieldLabels.length
                            ? `We found: ${suggestedFieldLabels.join(', ')}.`
                            : 'We did not find profile details, so you can enter them manually.'}</p>
                    </div>
                  </div>
                ) : null}
                {businessImports.length ? (
                  <div className="onboarding-imported-sources" role="region" aria-label="Imported sources">
                    <strong>Saved knowledge sources</strong>
                    <ul>
                      {importedSourceItems.map(source => (
                        <li key={`${source.sourceId || source.sourceType}-${source.name.trim().toLowerCase()}`}>
                          <span className="onboarding-source-type">{source.sourceType === 'website' ? 'Website' : 'Document'}</span>
                          <span className="onboarding-source-name" title={source.name}>{source.name}</span>
                          <em className={`onboarding-source-status status-${source.status || 'saved'}`}>
                            {onboardingSourceStatusLabel(source.status)}
                          </em>
                          {source.sourceId ? <button type="button" className="onboarding-delete-source" aria-label={`Delete ${source.name}`} onClick={() => setSourcePendingDeletion({ id: source.sourceId, title: source.name, sourceType: source.sourceType })}>Delete</button> : null}
                        </li>
                      ))}
                    </ul>
                    {hasFailedSources || hasProcessingSources ? <small role="status">{hasProcessingSources
                      ? `${processingSourceCount || 1} source${(processingSourceCount || 1) === 1 ? ' is' : 's are'} still processing${failedSourceCount ? `; ${failedSourceCount} ${failedSourceCount === 1 ? 'source has' : 'sources have'} already failed` : ''}. Review will unlock when processing finishes.`
                      : `${failedSourceCount || 1} source${(failedSourceCount || 1) === 1 ? ' needs' : 's need'} attention. You can still review suggestions from the sources that finished successfully.`}</small> : null}
                  </div>
                ) : null}
              </>}
              {profileSection.id === 'basics' && <>
                <div className="onboarding-profile-stage-intro">
                  <span>2 of 5</span>
                  <h2 ref={profileStageHeadingRef} tabIndex="-1">Let’s confirm the basics</h2>
                  <p>These are the details your Receptionist will use when greeting callers and answering simple questions.</p>
                </div>
                <div className="onboarding-form-grid single-column">{businessBasicsFields.map(renderProfileField)}</div>
              </>}
              {profileSection.id === 'services' && <>
                <div className="onboarding-profile-stage-intro">
                  <span>3 of 5</span>
                  <h2 ref={profileStageHeadingRef} tabIndex="-1">What do you help customers with?</h2>
                  <p>A short, natural description is enough. Your Receptionist can use it to understand why someone is calling.</p>
                </div>
                <div className="onboarding-form-grid single-column">{businessServicesFields.map(renderProfileField)}</div>
              </>}
              {profileSection.id === 'additional' && <>
                <div className="onboarding-profile-stage-intro">
                  <span>4 of 5</span>
                  <h2 ref={profileStageHeadingRef} tabIndex="-1">Anything else your Receptionist should know?</h2>
                  <p>Add team information, common questions, or context that helps your Receptionist sound informed. Everything here is optional.</p>
                </div>
                <div className="onboarding-form-grid">{additionalProfileFields.map(renderProfileField)}</div>
              </>}
              {profileSection.id === 'review' && <>
                <div className="onboarding-profile-stage-intro">
                  <span>5 of 5</span>
                  <h2 ref={profileStageHeadingRef} tabIndex="-1">Does this business profile look right?</h2>
                  <p>Edit any section that needs attention. Only the confirmed profile is applied to your Receptionist.</p>
                </div>
                <div className="onboarding-profile-summaries">
                  {businessProfileSections.slice(1, -1).map((section, index) => renderProfileSectionSummary(section, index + 1))}
                </div>
                <div className="onboarding-profile-ready"><span aria-hidden="true">✓</span><div><strong>Ready to save</strong><p>{answers.profile.businessName} has the essential information your Receptionist needs to continue.</p></div></div>
              </>}
            </div>
          </>}

          {activeStep === 'personalize' && <>
            <span className="say-eyebrow">Voice and greeting</span>
            <h1 ref={headingRef} tabIndex="-1">How should your Receptionist answer the phone?</h1>
            <p>Preview the greeting, then adjust the voice or wording until it sounds right.</p>
            <div className="onboarding-form-grid single-column">
              <div className="onboarding-greeting-field">
                <div className="onboarding-profile-field-heading">
                  <label htmlFor="onboarding-greeting">Greeting</label>
                  {greetingSuggestion ? <span className={answers.greeting === greetingSuggestion ? '' : 'edited'}>{answers.greeting === greetingSuggestion ? 'AI suggested' : 'Edited AI suggestion'}</span> : null}
                </div>
                <textarea id="onboarding-greeting" rows="4" value={answers.greeting || ''} placeholder={fallbackGreeting(answers.profile.businessName)} onChange={event => updateGreeting(event.target.value)} />
                {greetingSuggestion ? (
                  <div className="onboarding-greeting-suggestion-note">
                    <small>{aiRecommendations.greetingReason || 'AI generated from your confirmed business profile.'}</small>
                    {answers.greeting !== greetingSuggestion ? <button type="button" onClick={restoreGreetingSuggestion}>Restore suggestion</button> : null}
                  </div>
                ) : null}
              </div>
              <div className="onboarding-voice-field">
                <label htmlFor="onboarding-voice">Voice</label>
                <select id="onboarding-voice" value={answers.voiceId || ''} onChange={event => updateVoice(event.target.value)}><option value="" disabled>Choose a voice</option>{voices.map(voice => <option key={voice.id} value={voice.id}>{voice.name}</option>)}</select>
                <audio
                  ref={voicePreviewRef}
                  aria-label="Voice preview"
                  onCanPlayThrough={playLoadedVoicePreview}
                  onEnded={() => setVoicePreviewState('idle')}
                  onError={() => {
                    setVoicePreviewState('idle');
                    setVoicePreviewError('The preview could not be played. Try again in a moment.');
                  }}
                />
                <div className="onboarding-voice-preview-control">
                  {voicePreviewState === 'playing' ? (
                    <Button variant="secondary" onClick={stopVoicePreview}>Stop preview</Button>
                  ) : (
                    <Button variant="secondary" onClick={startVoicePreview} disabled={voicePreviewState === 'loading' || !answers.voiceId || !answers.greeting?.trim()} loading={voicePreviewState === 'loading'} loadingLabel={"Preparing preview…"}>
                      {'Play greeting'}
                    </Button>
                  )}
                  <small>Uses the selected voice and greeting above.</small>
                </div>
                {voicePreviewError ? <small className="onboarding-voice-preview-error" role="alert">{voicePreviewError}</small> : null}
              </div>
            </div>
          </>}

          {activeStep === 'call_recording' && <>
            <span className="say-eyebrow">Call history</span>
            <h1 ref={headingRef} tabIndex="-1">Would you like to record calls?</h1>
            <p>Recordings make it easier to review exactly what was said and confirm follow-up details.</p>
            <ChoiceCards
              options={CALL_RECORDING_CHOICES.map(option => ({
                ...option,
                disabled: option.id === 'enabled' && organization.recordingStorageReady === false,
              }))}
              value={answers.callRecording}
              onChange={value => setAnswers(current => ({ ...current, callRecording: value }))}
            />
            <div className="onboarding-recording-note">
              <span aria-hidden="true">♪</span>
              <div>
                <strong>Clear notice and controlled retention</strong>
                <p>If enabled, your Receptionist tells callers the call may be recorded. Recordings are private to authorized team members, kept for 90 days by default, and can be deleted from Call history.</p>
              </div>
            </div>
            {organization.recordingStorageReady === false ? <Alert tone="warning">Call recording is temporarily unavailable. Choose Not now to continue setup.</Alert> : null}
          </>}

          {activeStep === 'goal_setup' && <>
            <span className="say-eyebrow">Your selected paths</span>
            <h1 ref={headingRef} tabIndex="-1">Set up what your Receptionist will do</h1>
            <OnboardingGoalSetup goals={selectedGoals} paths={onboarding?.readiness?.paths || []} onRefresh={refreshSetupSnapshot} onBusyChange={setSetupBusy} onReviewChange={setSetupReview} onBack={goBack} />
          </>}

          {activeStep === 'review' && <>
            <span className="say-eyebrow">Review and publish</span>
            <h1 ref={headingRef} tabIndex="-1">Your Receptionist is ready to publish</h1>
            <p>Review the essentials, then publish them for calls before choosing your phone number.</p>
            <div className="onboarding-review-grid">
              <article><small>Caller outcomes</small><strong>{selectedGoals.map(goal => GOALS.find(item => item.id === goal)?.title).join(', ')}</strong></article>
              {(onboarding?.readiness?.paths || []).map(path => <article key={path.goal}><small>{path.label}</small><strong>{path.ready ? 'Ready' : 'Needs setup'}</strong>{path.requirements.filter(item => !item.ready).map(item => <span key={item.label}>{item.label}</span>)}</article>)}
              <article><small>Business</small><strong>{answers.profile.businessName || organization.businessName}</strong><span>{answers.profile.businessLocation || organization.businessLocation}</span></article>
              <article><small>Greeting</small><strong>“{answers.greeting || organization.greeting}”</strong></article>
              <article><small>Call recording</small><strong>{answers.callRecording === 'enabled' ? 'Enabled' : 'Not enabled'}</strong>{answers.callRecording === 'enabled' ? <span>90-day retention by default</span> : null}</article>
            </div>
          </>}

          {activeStep === 'phone_number' && <>
            <span className="say-eyebrow">Your receptionist number</span>
            <h1 ref={headingRef} tabIndex="-1">Choose your Receptionist’s phone number</h1>
            <p>Pick a local or toll free number. Your setup will continue here as soon as it is selected.</p>
            <PhoneNumberSelector
              defaultAreaCode={answers.profile.businessPhoneNumber || organization.businessPhoneNumber}
              onClaimed={handlePhoneNumberClaimed}
            />
          </>}

          {activeStep === 'first_call' && onboarding?.status !== 'completed' && <>
            <span className="say-eyebrow">Your receptionist is ready</span>
            <h1 ref={headingRef} tabIndex="-1">Let’s hear your Receptionist in action</h1>
            <p>Ask a question a real customer would ask, then try the short follow-up intake. The call stays on this page and setup continues automatically when you finish.</p>
            <div className="onboarding-call-number"><small>Your Sayable number</small><strong>{formatAmericanPhoneNumber(organization.provisionedNumber)}</strong></div>
            <BrowserTestCall onCallEnded={waitForCallCompletion} />
          </>}

          {onboarding?.status === 'completed' && <>
            <span className="say-eyebrow">Setup complete</span>
            <h1 ref={headingRef} tabIndex="-1">Your Receptionist is ready for callers</h1>
            <p>Your first call was received successfully. Your setup plan will stay available from the overview.</p>
            <div className="onboarding-celebration" aria-hidden="true">✓</div>
            <Button size="lg" onClick={() => navigate('/overview')}>Go to overview</Button>
          </>}

          {error ? <Alert tone="danger" title="Setup needs attention">{error}</Alert> : null}

          {!['phone_number', 'first_call'].includes(activeStep) && (activeStep !== 'goal_setup' || setupReview) && onboarding?.status !== 'completed' ? (
            <div className="onboarding-actions">
              {activeStep !== 'goal_setup' && <Button variant="quiet" onClick={goBack} disabled={progress.index === 0 || saving || (activeStep === 'goal_setup' && (setupBusy || setupDraftSaving))}>Back</Button>}
              <Button
                size="lg"
                onClick={continueStep}
                disabled={!canContinue() || saving || importing}
                loading={saving}
                loadingLabel={activeStep === 'review'
                  ? 'Publishing…'
                  : activeStep === 'business_profile' && profileSection.id === 'review'
                    ? 'Confirming profile…'
                    : 'Saving…'}
              >
                {continueButtonLabel}
              </Button>
            </div>
          ) : null}
        </section>
      </main>
      {sourcePendingDeletion ? (
        <OnboardingSourceDeleteDialog
          source={sourcePendingDeletion}
          onClose={() => setSourcePendingDeletion(null)}
          onConfirm={deleteImportedSource}
        />
      ) : null}
    </div>
  );
}

export default OnboardingRedesign;
